import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  PiArrowRight,
  PiFileText,
  PiCurrencyEur,
  PiPercent,
  PiWarning,
  PiArchive,
  PiShield,
} from 'react-icons/pi'

import ButtonLink from '@components/common/Button/ButtonLink'
import ProgressBar from '@components/common/ProgressBar/ProgressBar'
import HeaderBack from '@/components/dashboard/Header/HeaderBack'

import MotionText from '@/components/common/Motion/MotionText'

import { useKycStore } from '@store/useKycStore'
import MotionDiv from '@/components/common/Motion/MotionDiv'

const HandbookDocumentsPage = () => {
  const { t } = useTranslation()
  const progress = useKycStore((state) => state.progress)
  const setProgress = useKycStore((state) => state.setProgress)

  useEffect(() => setProgress(4), [])

  return (
    <div className="h-full bg-white p-5 flex flex-col">
      <HeaderBack className='items-center gap-4'>
        <ProgressBar
          currentStep={progress}
          totalSteps={4}
        />
      </HeaderBack>
      <MotionText
        as="h1"
        className="text-base-black text-2xl font-semibold leading-tight my-6"
      >
        {t('handbook.documents.title')}
      </MotionText>

      <MotionText
        as="h2"
        className="text-base-black text-base font-semibold tracking-tight mb-2"
      >
        {t('handbook.documents.tripInvoices.title')}
      </MotionText>

      <MotionText
        as="p"
        className="text-zinc-700 text-sm mb-6"
      >
        {t('handbook.documents.tripInvoices.description')}
      </MotionText>

      <MotionDiv className='flex flex-col space-y-6 pb-24'>
        <MotionDiv className='flex gap-4'>
          <PiArrowRight className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.documents.tripInvoices.path')}
          </MotionText>
        </MotionDiv>

        <MotionDiv className='flex gap-4'>
          <PiFileText className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.documents.tripInvoices.format')}
          </MotionText>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-base-black text-base font-semibold tracking-tight mt-2 mb-2"
        >
          {t('handbook.documents.commissionInvoice.title')}
        </MotionText>

        <MotionText
          as="p"
          className="text-zinc-700 text-sm mb-4"
        >
          {t('handbook.documents.commissionInvoice.description')}
        </MotionText>

        <MotionDiv className='flex gap-4'>
          <PiArrowRight className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.documents.commissionInvoice.path')}
          </MotionText>
        </MotionDiv>

        <MotionDiv className='flex gap-4'>
          <PiFileText className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.documents.commissionInvoice.format')}
          </MotionText>
        </MotionDiv>

        <MotionDiv className='flex gap-4'>
          <PiPercent className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.documents.commissionInvoice.deductible')}
          </MotionText>
        </MotionDiv>

        <MotionDiv className='flex gap-4'>
          <PiCurrencyEur className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.documents.commissionInvoice.vatIncluded')}
          </MotionText>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-base-black text-base font-semibold tracking-tight mt-2 mb-2"
        >
          {t('handbook.documents.monthlySummary.title')}
        </MotionText>

        <MotionText
          as="p"
          className="text-zinc-700 text-sm mb-4"
        >
          {t('handbook.documents.monthlySummary.description')}
        </MotionText>

        <MotionDiv className='flex gap-4'>
          <PiArrowRight className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.documents.monthlySummary.path')}
          </MotionText>
        </MotionDiv>

        <MotionDiv className='flex gap-4'>
          <PiCurrencyEur className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.documents.monthlySummary.tipsIncome')}
          </MotionText>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-base-black text-base font-semibold tracking-tight mt-2 mb-2"
        >
          {t('handbook.documents.expenseProof.title')}
        </MotionText>

        <MotionText
          as="p"
          className="text-zinc-700 text-sm mb-4"
        >
          {t('handbook.documents.expenseProof.description')}
        </MotionText>

        <MotionText
          as="p"
          className="text-zinc-700 text-sm mb-4"
        >
          {t('handbook.documents.expenseProof.accepted')}
        </MotionText>

        <MotionText
          as="h3"
          className="text-base-black text-sm font-semibold tracking-tight mb-4"
        >
          {t('handbook.common.important')}
        </MotionText>

        <MotionDiv className='flex gap-4'>
          <PiShield className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.documents.expenseProof.requirements')}
          </MotionText>
        </MotionDiv>

        <MotionDiv className='flex gap-4'>
          <PiArchive className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.documents.expenseProof.retention')}
          </MotionText>
        </MotionDiv>

        <MotionDiv className='flex gap-4'>
          <PiWarning className='min-w-6 h-6' />
          <MotionText
            as="p"
            className="text-zinc-700 text-sm"
          >
            {t('handbook.documents.expenseProof.taxBenefit')}
          </MotionText>
        </MotionDiv>
      </MotionDiv>

      <motion.div
        className="absolute bottom-0 -ml-5 px-5 pb-5 pt-8 w-full bg-linear-to-t from-white via-white to-transparent from-0% via-40% to-100%"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          duration: 0.2,
          ease: "easeOut"
        }}
      >
        <ButtonLink variant='primary' to={'/dashboard'}>
          {t('common.ok')}
        </ButtonLink>
      </motion.div>
    </div>
  )
}

export default HandbookDocumentsPage